import { useCallback, useEffect, useRef } from "react";
import { Flex, Heading } from "@/ui";
import Time from "@/lib/Time";
import { dataSyncEmitter } from "@/modules/events/emitter";
import { useIsMobileView } from "@/hooks";
import { useEventDeletion } from "viewer/store/eventDeletion";
import { useActivityLog } from "viewer/store/activityLog";
import ActivityLogController from "../ActivityLogController";
import EventLoading from "./EventLoading";
import EventCard from "./EventCard";
import EventListBanner from "./EventListBanner";
import DeletionPanel from "./DeletionPanel";

export default function EventList() {
  const [events, selectedEventId, setSelectedEventId, loading, reachedEnd] = useActivityLog((state) => [
    state.events,
    state.selectedEventId,
    state.setSelectedEventId,
    state.loading,
    state.reachedEnd
  ]);
  const isInDeletionMode = useEventDeletion((s) => s.isInDeletionMode);
  const listRef = useRef<HTMLDivElement>(null);
  const blockScrollListener = useRef(false);
  const isMobile = useIsMobileView();

  useEffect(() => {
    if (loading) blockScrollListener.current = true;
    else blockScrollListener.current = false;
  }, [loading]);

  const onScroll = useCallback(() => {
    const list = listRef.current;
    if (!list || reachedEnd) return;
    if (blockScrollListener.current) return;
    if (Math.abs(list.scrollHeight - list.clientHeight - list.scrollTop) < 300) {
      blockScrollListener.current = true;
      ActivityLogController.downloadNextEvents({
        callback: () => {
          blockScrollListener.current = false;
        }
      });
    }
  }, [reachedEnd]);

  let displayDateRef: string | null = null;

  return (
    <Flex
      w={["100%", "calc(50% - 0.5rem)", "calc(50% - 0.75rem)"]}
      bg="surface1"
      borderRadius="1rem"
      rowGap="0.75rem"
      direction="column"
    >
      {isInDeletionMode && <DeletionPanel />}
      <Flex
        ref={listRef}
        direction="column"
        w="100%"
        rowGap="0.75rem"
        overflowY="scroll"
        pt="1rem"
        px={["1rem", "0.75rem", "1rem"]}
        pb={reachedEnd && !loading ? "1rem" : 0}
        maxH="100%"
        onScroll={onScroll}
      >
        <EventListBanner />
        <Flex direction="column" rowGap="0.75rem">
          {events.map((event) => {
            const date = Time.getRelevantDate(event.start);
            const time =
              date !== displayDateRef ? (
                <Heading as="h5" mb="1rem">
                  {date}
                </Heading>
              ) : null;
            displayDateRef = date;
            return (
              <Flex key={event.uniqueId} direction="column">
                {time}
                <EventCard
                  onSelect={() => {
                    setSelectedEventId(event.uniqueId);
                    isMobile && dataSyncEmitter.emit("open-event-modal");
                  }}
                  isSelected={selectedEventId === event.uniqueId}
                  event={event}
                />
              </Flex>
            );
          })}
          <EventLoading />
        </Flex>
      </Flex>
    </Flex>
  );
}
